// OpenerEditorPanel — schema-driven controls for the active opener preset.
// Reads draft from OpenerDraftContext, never owns its own copy of settings.

import React, { useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useOpenerDraft } from './OpenerDraftContext';
import { getPreset, PRESET_LIST } from '../../openers/openerRegistry';

const API = process.env.REACT_APP_BACKEND_URL;

// Read a deep path like "openingTypography.color" from a settings object.
const getByPath = (obj, path) => {
  if (!path) return undefined;
  return String(path).split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
};

const COUNTER_COLOR = (len, max) => {
  if (len >= max) return 'text-red-600 dark:text-red-400 font-semibold';
  if (len >= Math.floor(max * 0.9)) return 'text-amber-600 dark:text-amber-400';
  return 'text-gray-400';
};

function Control({ ctrl, eventId }) {
  const draft = useOpenerDraft();
  const value = getByPath(draft.draftSettings, ctrl.id) ?? ctrl.default;

  const onChange = (v) => draft.setDraftSetting(ctrl.id, v);

  // Phase 1.9.13 — `dependsOn: 'someBooleanKey'` hides this control
  // when the dependency is falsy. Saved values are preserved on the
  // draft so the previous configuration is restored when the dep flips
  // back on.
  if (ctrl.dependsOn) {
    const depVal = getByPath(draft.draftSettings, ctrl.dependsOn);
    if (!depVal) return null;
  }

  const replayHint = !ctrl.liveUpdate && (
    <span className="text-[9px] text-amber-600 dark:text-amber-400 ml-2">Replay to apply</span>
  );

  if (ctrl.type === 'toggle') {
    return (
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{ctrl.label}</span>
          <button
            type="button"
            onClick={() => onChange(!value)}
            role="switch"
            aria-checked={!!value}
            data-testid={`opener-ctrl-${ctrl.id}`}
            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${value ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`}
          >
            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${value ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {ctrl.hint && <p className="text-[9px] text-gray-400 mt-0.5">{ctrl.hint}</p>}
      </div>
    );
  }

  if (ctrl.type === 'group') {
    return (
      <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800">
        <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">{ctrl.label}</label>
        <div className="space-y-2.5 pl-2 border-l-2 border-blue-200 dark:border-blue-900/40">
          {ctrl.children.map((c) => <Control key={c.id} ctrl={c} eventId={eventId} />)}
        </div>
      </div>
    );
  }

  if (ctrl.type === 'textarea' || ctrl.type === 'text') {
    const max = ctrl.maxLength || (ctrl.type === 'textarea' ? 200 : 80);
    const v = String(value || '');
    const len = v.length;
    return (
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
          <span>{ctrl.label}{replayHint}</span>
          <span className={`font-mono text-[10px] ${COUNTER_COLOR(len, max)}`}>{len} / {max}</span>
        </label>
        {ctrl.type === 'textarea' ? (
          <textarea
            value={v}
            maxLength={max}
            rows={3}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40"
            data-testid={`opener-ctrl-${ctrl.id}`}
          />
        ) : (
          <input
            type="text"
            value={v}
            maxLength={max}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40"
            data-testid={`opener-ctrl-${ctrl.id}`}
          />
        )}
        {ctrl.hint && <p className="text-[9px] text-gray-400 mt-0.5">{ctrl.hint}</p>}
      </div>
    );
  }

  if (ctrl.type === 'range') {
    // Phase 1.9.9 — `format: 'percent'` displays the value as a
    // percentage (e.g. 0.94 → "94%") without changing how it's stored.
    // Phase 1.9.15 — `format: 'ms'` displays milliseconds as "Xms".
    let displayValue;
    if (typeof value === 'number') {
      if (ctrl.format === 'percent') displayValue = `${Math.round(value * 100)}%`;
      else if (ctrl.format === 'px') displayValue = `${Math.round(value)}px`;
      else if (ctrl.format === 'ms') displayValue = `${Math.round(value)}ms`;
      else displayValue = value.toFixed(ctrl.step < 1 ? 2 : 0);
    } else {
      displayValue = value;
    }
    return (
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
          <span>{ctrl.label}{replayHint}</span>
          <span className="text-gray-400">{displayValue}</span>
        </label>
        <input
          type="range"
          min={ctrl.min} max={ctrl.max} step={ctrl.step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full accent-blue-500 h-1.5"
          data-testid={`opener-ctrl-${ctrl.id}`}
        />
        {ctrl.hint && <p className="text-[9px] text-gray-400 mt-0.5">{ctrl.hint}</p>}
      </div>
    );
  }

  if (ctrl.type === 'select') {
    return (
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">{ctrl.label}{replayHint}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="opener-ctrl-select w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40"
          style={{ WebkitTextFillColor: 'currentColor' }}
          data-testid={`opener-ctrl-${ctrl.id}`}
        >
          {ctrl.options.map((o) => (
            <option key={o.value} value={o.value} className="text-gray-900 bg-white">{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (ctrl.type === 'color') {
    return (
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">{ctrl.label}</label>
        <div className="flex items-center gap-2">
          <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-8 h-6 rounded cursor-pointer" data-testid={`opener-ctrl-${ctrl.id}`} />
          <span className="font-mono text-[10px] text-gray-500">{value}</span>
        </div>
      </div>
    );
  }

  if (ctrl.type === 'font') {
    return (
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">{ctrl.label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="opener-ctrl-select w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/40"
          style={{ fontFamily: `'${value}', serif`, WebkitTextFillColor: 'currentColor' }}
          data-testid={`opener-ctrl-${ctrl.id}`}
        >
          {(ctrl.fontList || []).map((f) => (
            <option key={f} value={f} className="text-gray-900 bg-white" style={{ fontFamily: `'${f}', serif` }}>{f}</option>
          ))}
        </select>
      </div>
    );
  }

  if (ctrl.type === 'image') {
    return <ImageControl ctrl={ctrl} value={value} onChange={onChange} draft={draft} eventId={eventId} />;
  }

  return null;
}

function ImageControl({ ctrl, value, onChange, draft, eventId }) {
  const fileRef = useRef(null);
  // Phase 1.9.4 — surgical upload-UX hardening per real-world report:
  //   • visible loading state (button disabled, "Uploading…" label)
  //   • type="button" on both buttons (defensive against form submit)
  //   • error path keeps the previous image (no implicit clear)
  //   • surfaces the backend error detail instead of a generic toast
  //   • resets the file input so the same file can be picked again
  const [uploading, setUploading] = React.useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const inputEl = e.target;
    const previousValue = value; // preserved if upload fails
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/events/${eventId}/rsvp-page/upload-image`, fd, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const url = res.data?.url || res.data?.image_url;
      // Phase 1.9.7 — log the deployment fingerprint so DevTools shows
      // which build of the backend actually answered the request. If a
      // 520 occurs AND this log never appears, the request never reached
      // the new handler (proxy/CF/build mismatch). If this logs but a
      // later step fails, the new handler IS live.
      console.log('[opener-upload] handlerVersion=', res.data?.handlerVersion, 'requestId=', res.data?.requestId, 'status=', res.status);
      if (url) {
        onChange(url);
        // Phase 1.9.16 — declarative side effects. The control schema
        // may declare `onUploadSet: { someKey: value }` to update sibling
        // settings on this preset (e.g. Rain flips `backgroundMode` to
        // 'image'). Presets that don't need this side effect simply omit
        // the key — the generic control writes nothing extra.
        if (ctrl.onUploadSet && typeof ctrl.onUploadSet === 'object') {
          Object.entries(ctrl.onUploadSet).forEach(([k, v]) => draft.setDraftSetting(k, v));
        }
        toast.success('Background image uploaded — click "Save opener" to persist');
      } else {
        // Defensive: backend returned 200 but no URL — keep the previous value.
        onChange(previousValue);
        toast.error('Upload returned no URL');
      }
    } catch (err) {
      // Preserve previous image. Surface the actual backend error / status.
      onChange(previousValue);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (status === 413) {
        msg = 'Image too large. Try one under 15 MB.';
      } else if (status === 400) {
        msg = 'Unsupported image format. Use JPG, PNG or WebP.';
      } else if (status === 500 || status === 502 || status === 503 || status === 520) {
        msg = `Upload failed (${status}). Try a smaller image or retry in a moment.`;
      } else if (!status) {
        // No HTTP status reached the client → connection/proxy issue.
        msg = 'Upload failed — network or server issue. Try again or use a smaller image.';
      } else {
        msg = `Upload failed (${status})`;
      }
      toast.error(msg);
    } finally {
      setUploading(false);
      // Reset the input so picking the same file again still fires change.
      if (inputEl) inputEl.value = '';
    }
  };
  return (
    <div>
      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">{ctrl.label}</label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="text-[11px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 disabled:opacity-60 disabled:cursor-wait"
          data-testid={`opener-ctrl-${ctrl.id}`}
        >
          {uploading ? 'Uploading…' : (value ? 'Replace image' : 'Upload image')}
        </button>
        {value && !uploading && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              // Phase 1.9.16 — declarative removal side effects. Schema
              // may declare `onRemoveSet: { someKey: value }` to roll
              // back any flags it set on upload (e.g. Rain restores
              // `backgroundMode: 'paper'`). Generic — no preset coupling.
              if (ctrl.onRemoveSet && typeof ctrl.onRemoveSet === 'object') {
                Object.entries(ctrl.onRemoveSet).forEach(([k, v]) => draft.setDraftSetting(k, v));
              }
            }}
            className="text-[11px] px-2 py-1 rounded text-red-600 hover:bg-red-50"
            data-testid={`opener-ctrl-${ctrl.id}-remove`}
          >
            Remove
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={ctrl.accept || 'image/*'}
        onChange={handleFile}
        className="hidden"
        data-testid={`opener-ctrl-${ctrl.id}-file`}
      />
      {ctrl.hint && <p className="text-[9px] text-gray-400 mt-0.5">{ctrl.hint}</p>}
    </div>
  );
}

export default function OpenerEditorPanel({ eventId }) {
  const draft = useOpenerDraft();
  if (!draft) return null;
  const preset = getPreset(draft.presetId);

  // Phase 1.9.16 — preset switcher. When more than one preset is
  // registered, designers can switch via a <select>. Switching resets
  // the draft to the target preset's defaults (we don't merge across
  // presets — see OpenerDraftContext.switchPreset).
  const handlePresetChange = async (nextId) => {
    if (!nextId || nextId === draft.presetId) return;
    if (draft.isDirty) {
      const ok = window.confirm('Switching presets will replace the current unsaved opener settings. Continue?');
      if (!ok) return;
    }
    draft.switchPreset(nextId);
  };

  // Phase 1.9.1 — explicit Enable toggle at the top of the panel.
  // Three states:
  //   1) No saved opener yet → CTA "Enable opening animation"
  //   2) Saved + disabled → toggle off, message, no controls
  //   3) Saved + enabled  → toggle on, full controls
  if (!draft.hasSavedOpener) {
    // Phase 1.9.16 — empty state is fully registry-driven. No Rain copy.
    // With 1 preset registered we render a single Enable CTA.
    // With ≥2 presets we render a small picker first, then Enable.
    const multi = PRESET_LIST.length > 1;
    const activeName = preset?.name || 'opening animation';
    return (
      <div className="p-3 space-y-2" data-testid="opener-panel-empty">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Add a cinematic opener that plays once before the invitation appears.
          You can turn it off any time without losing your settings.
        </p>
        {multi && (
          <div data-testid="opener-empty-picker">
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Choose a preset</label>
            <select
              value={draft.presetId || ''}
              onChange={(e) => draft.switchPreset(e.target.value)}
              className="opener-ctrl-select w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
              style={{ WebkitTextFillColor: 'currentColor' }}
              data-testid="opener-empty-preset-select"
            >
              {PRESET_LIST.map((p) => (
                <option key={p.id} value={p.id} className="text-gray-900 bg-white">{p.name}</option>
              ))}
            </select>
            {preset?.description && (
              <p className="text-[10px] text-gray-400 mt-1">{preset.description}</p>
            )}
          </div>
        )}
        <button
          onClick={async () => { await draft.setEnabled(true); toast.success('Opening animation enabled'); }}
          disabled={!draft.presetId}
          className="w-full py-1.5 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="opener-enable-btn"
        >
          {multi ? 'Enable opening animation' : `Enable ${activeName}`}
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3">
      {/* Enable / disable toggle — always visible once saved at least once. */}
      <div className="flex items-center justify-between gap-2" data-testid="opener-enabled-row">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-gray-900 dark:text-gray-100">Enable opening animation</div>
          <div className="text-[10px] text-gray-400">{draft.enabled ? 'Plays once for each guest.' : 'Guests will enter the invitation directly.'}</div>
        </div>
        {/* Preview as guest — saves the current draft first so the live
            preview URL reflects unsaved settings, then opens the existing
            authenticated preview-link in a new tab. Hidden when the
            opener is toggled off (nothing to preview). */}
        {draft.enabled && (
          <button
            onClick={async () => {
              try {
                if (draft.isDirty) await draft.save();
                const tk = localStorage.getItem('token');
                const res = await axios.post(
                  `${API}/api/events/${eventId}/rsvp-page/preview-link`,
                  {},
                  { headers: { Authorization: `Bearer ${tk}` } }
                );
                const baseUrl = `${window.location.origin}${res.data.preview_url}`;
                const sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
                window.open(`${baseUrl}${sep}v=${Date.now()}`, '_blank', 'noopener,noreferrer');
              } catch (err) {
                toast.error('Could not open preview');
              }
            }}
            className="shrink-0 text-[10px] px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            data-testid="opener-preview-as-guest-btn"
            title="Open the invitation in a new tab as a guest sees it (preview bypasses the seen-once gate)."
          >
            Preview as guest
          </button>
        )}
        <button
          onClick={async () => {
            const next = !draft.enabled;
            await draft.setEnabled(next);
            toast.success(next ? 'Opening animation enabled' : 'Opening animation disabled');
          }}
          role="switch"
          aria-checked={draft.enabled}
          data-testid="opener-enabled-toggle"
          className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${draft.enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${draft.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {!draft.enabled ? (
        <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-3" data-testid="opener-disabled-message">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Guests will enter the invitation directly. Your preset and settings are kept — turn the toggle on to bring the opener back.
          </p>
        </div>
      ) : (
        <>
          {/* Phase 1.9.16 — preset switcher. Read-only name when only one
              preset is registered; a <select> with switchPreset() and
              unsaved-changes confirm when ≥ 2 are registered. */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Preset</label>
            {PRESET_LIST.length > 1 ? (
              <select
                value={draft.presetId || ''}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="opener-ctrl-select w-full text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                style={{ WebkitTextFillColor: 'currentColor' }}
                data-testid="opener-preset-switcher"
              >
                {PRESET_LIST.map((p) => (
                  <option key={p.id} value={p.id} className="text-gray-900 bg-white">{p.name}</option>
                ))}
              </select>
            ) : (
              <div className="text-xs font-medium" data-testid="opener-active-preset">{preset?.name || draft.presetId}</div>
            )}
            {PRESET_LIST.length > 1 && draft.isDirty && (
              <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-1">Switching replaces unsaved settings.</p>
            )}
          </div>

          {/* Schema-driven controls */}
          {preset?.schema?.map((c) => <Control key={c.id} ctrl={c} eventId={eventId} />)}

          {/* Tested combinations */}
          <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800">
            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Tested combinations</label>
            <div className="grid grid-cols-2 gap-1">
              {(preset?.combos || []).map((c) => (
                <button
                  key={c.id}
                  onClick={() => draft.applyCombo(c)}
                  className="py-1.5 rounded text-[10px] font-medium bg-gray-50 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-300"
                  data-testid={`opener-combo-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bottom actions */}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={draft.replay}
                className="py-1.5 rounded text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="opener-replay-btn"
              >
                ▶ Replay
              </button>
              <button
                onClick={draft.resetToDefaults}
                className="py-1.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-300"
                data-testid="opener-reset-btn"
              >
                Reset
              </button>
            </div>
            <button
              onClick={async () => { await draft.save(); toast.success('Opener saved'); }}
              disabled={!draft.isDirty}
              className="w-full py-1.5 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="opener-save-btn"
            >
              {draft.isDirty ? 'Save opener' : 'Saved'}
            </button>
          </div>
        </>
      )}

      {/* Remove (hard delete) — always available; clears settings entirely. */}
      <button
        onClick={async () => { await draft.remove(); toast('Opener removed'); }}
        className="w-full py-1 rounded text-[11px] text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
        data-testid="opener-remove-btn"
      >
        Remove opener (clears settings)
      </button>
    </div>
  );
}
